import axios from 'axios';
import { useQuery, useMutation, useQueryClient } from 'react-query';

const API_BASE_URL = '/api';
// Fetch time-off requests
export const useTimeOffRequests = (token) => {
    return useQuery('timeOffRequests', async () => {
        const response = await axios.get(`${API_BASE_URL}/time-off-request/view`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    }, {
        enabled: !!token,
        staleTime: 2 * 60 * 1000,
        cacheTime: 10 * 60 * 1000,
        refetchOnWindowFocus: false,
    });
};

export const useTimeOffRequestsOptimized = ({
    token,
    currentPage,
    limit,
    startDate,
    endDate,
    users,
    status,
    filter,
    enabled = true,
}) => {
    return useQuery(
        [
            'timeOffRequestsOptimized',
            { currentPage, limit, startDate, endDate, users, status, filter },
        ],
        async () => {
            const response = await axios.post(
                `${API_BASE_URL}/time-off-request/view-optimize`,
                {
                    page: currentPage,
                    limit,
                    startDate,
                    endDate,
                    users,
                    status,
                    filter,
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            return response.data;
        },
        {
            staleTime: 5 * 60 * 1000, // Data is fresh for 5 minutes
            cacheTime: 10 * 60 * 1000, // Data remains in cache for 10 minutes
            keepPreviousData: false, // Show skeleton during page changes
            enabled: !!token && enabled, // Only fetch if token exists and enabled
            retry: 2, // Retry failed requests up to 2 times
            refetchOnWindowFocus: false, // Don't refetch on window focus
        }
    );
};

export const getTimeOff = async (token, currentPage, limit, startDate, endDate, users, status, filter) => {
    const response = await axios.post(
        `${API_BASE_URL}/time-off-request/view-optimize`,
        {
            page: currentPage,
            limit,
            startDate,
            endDate,
            users,
            status,
            filter,
        },
        {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        }
    );
    return response.data;
}

export function useTimeOffId(token) {
    return useQuery('timeOffId', async () => {
        const response = await axios.get('/api/time-off/id', {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });

        return response.data.time_off_request_id;
    });
}

// Fetch user by ID
export const useUserById = (token) => {
    return useQuery('userById', async () => {
        const response = await axios.get(`${API_BASE_URL}/team`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        const sortedTeams = response.data.sort((a, b) => {
            const nameA = a.first_name ? `${a.first_name} ${a.middle_name ? a.middle_name : ''} ${a.last_name}` : `${a.company_first_name ? a.company_first_name + (a.company_last_name && a.company_last_name) : a.company_name}`;
            const nameB = b.first_name ? `${b.first_name} ${b.middle_name ? b.middle_name : ''} ${b.last_name}` : `${b.company_first_name ? b.company_first_name + (b.company_last_name && b.company_last_name) : b.company_name}`;
            return nameA.localeCompare(nameB);
        });

        return sortedTeams;
    }, { staleTime: 5 * 60 * 1000, cacheTime: 10 * 60 * 1000, refetchOnWindowFocus: false });
};

export const useActiveUser = (token) => {
    return useQuery('activeUser', async () => {
        const response = await axios.get(`${API_BASE_URL}/active/team`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        const sortedTeams = response.data.sort((a, b) => {
            const nameA = a.first_name ? `${a.first_name} ${a.middle_name ? a.middle_name : ''} ${a.last_name}` : `${a.company_first_name ? a.company_first_name + (a.company_last_name && a.company_last_name) : a.company_name}`;
            const nameB = b.first_name ? `${b.first_name} ${b.middle_name ? b.middle_name : ''} ${b.last_name}` : `${b.company_first_name ? b.company_first_name + (b.company_last_name && b.company_last_name) : b.company_name}`;
            return nameA.localeCompare(nameB);
        });

        return sortedTeams;
    }, { staleTime: 5 * 60 * 1000, cacheTime: 10 * 60 * 1000, refetchOnWindowFocus: false });
};

// Fetch leave types
export const useFetchLeaveTypes = (token) => {
    return useQuery('leaveTypes', async () => {
        const response = await axios.get(`${API_BASE_URL}/leave-type`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    }, {
        enabled: !!token,
        staleTime: 10 * 60 * 1000,
        cacheTime: 30 * 60 * 1000,
        refetchOnWindowFocus: false,
    });
};

// Approve time-off request
export const useApproveTimeOffRequest = (token) => {
    const queryClient = useQueryClient();
    return useMutation(
        (selectedId) =>
            axios.get(`/api/time-off-request/approve/${selectedId}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('timeOffRequests');
                queryClient.invalidateQueries('timeOffRequestsOptimized');
            },
        }
    );
};

// Reject a time-off request
export const useRejectTimeOffRequest = (token) => {
    const queryClient = useQueryClient();
    return useMutation(
        (selectedId) =>
            axios.get(`/api/time-off-request/reject/${selectedId}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('timeOffRequests');
                queryClient.invalidateQueries('timeOffRequestsOptimized');
            },
        }
    );
};

// Cancel a time-off request
export const useCancelTimeOffRequest = (token) => {
    const queryClient = useQueryClient();
    return useMutation(
        (selectedId) =>
            axios.get(`/api/time-off-request/cancel/${selectedId}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('timeOffRequests');
                queryClient.invalidateQueries('timeOffRequestsOptimized');
            },
        }
    );
};

// Delete a time-off request
export const useDeleteTimeOffRequest = (token) => {
    const queryClient = useQueryClient();
    return useMutation(
        (selectedId) =>
            axios.delete(`/api/time-off-request/delete/${selectedId}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('timeOffRequests');
                queryClient.invalidateQueries('timeOffRequestsOptimized');
            },
        }
    );
};

// Create a time-off request
export const useCreateTimeOffRequest = (token) => {
    const queryClient = useQueryClient(); // Get the queryClient
    return useMutation(
        (requestData) =>
            axios.post('/api/time-off-request/create', requestData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }),
        {
            onSuccess: () => {
                // Invalidate both query keys to refetch data
                queryClient.invalidateQueries('timeOffRequests');
                queryClient.invalidateQueries('timeOffRequestsOptimized');
            },
        }
    );
};

export const useUpdateTimeOffRequest = (token) => {
    const queryClient = useQueryClient(); // Get the queryClient
    return useMutation(
        ({ selectedId, formData }) =>
            axios.put(`/api/time-off-request/update/${selectedId}`, formData, {
                headers: { Authorization: `Bearer ${token}` },
            }),
        {
            onSuccess: () => {
                // Invalidate both query keys to refetch data
                queryClient.invalidateQueries("timeOffRequests");
                queryClient.invalidateQueries('timeOffRequestsOptimized');
            },
        }
    );
}; 